import Loader from "components/Loading";
import NoDataAvailable from "components/NoDataLightTheme";
import AssignChainStoreApproval from "components/request-dashboard/AssignChainStoreApproval";
import AssignmentCard from "components/request-dashboard/AssignmentCard";
import RejectModal from "components/request-dashboard/RejectModal";
import RequestAuditLogs from "components/request-dashboard/RequestAuditLogs";
import RequestHeader from "components/request-dashboard/RequestHeader";
import RequestInfoSidebar from "components/request-dashboard/RequestSidebar";
import RequestStepper from "components/request-dashboard/RequestStepper";
import SystemAdminApproval from "components/request-dashboard/SystemAdminApproval";
import Constants from "helpers/constants";
import { Component } from "react";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { AuthUser } from "state/auth_user";
import {
    fetchAuditRequestApprovalLog,
    fetchSystemAdmins,
    getRequestApprovalStages,
    RequestDependenciesState,
    updateRequestApprovalStatus,
    updateRequestApprover,
} from "state/request_details_dependencies";
import { getStepFromStatus, getSteps } from "utils/helpers";
import SelectChainStoreInfo, { AssignmentData } from "../../components/request-dashboard/SelectChainStoreInfo";
import { fetchRequestDetails, RequestDetails, RequestDetailsState } from "../../state/request_details";
import "../../styles/request_dashboard.scss";
 
// Constants
 
const STAGE_ORDER = { FIRST: 1, SECOND: 2, THIRD: 3 } as const;
 
const MESSAGE_STYLES: Record<string, React.CSSProperties> = {
    success: { backgroundColor: "#d1fae5", borderColor: "#10b981", color: "#065f46" },
    rejected: { backgroundColor: "#fee2e2", borderColor: "#ef4444", color: "#991b1b" },
    error: { backgroundColor: "#fef3c7", borderColor: "#f59e0b", color: "#92400e" },
};
 
const DEFAULT_MESSAGE_STYLE: React.CSSProperties = {
    backgroundColor: "#f3f4f6",
    borderColor: "#d1d5db",
    color: "#1f2937",
};
 
const CHAIN_ACCESS_REQUEST_TYPES = new Set([
    Constants.CHAIN_ACCESS,
    Constants.CHAIN_ACCESS_BY_EXTERNAL_PROJECT_ADMIN,
    Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_ANONYMOUS,
]);
 
 
 
// Helpers
 
const getInitialSelectedAdmin = (approvalStages: any): string => {
    const id = approvalStages?.assignedAdminId ?? approvalStages?.approver_user_id;
    return id != null ? String(id) : "all";
};
 
const parseRequestChain = (requestChain: any) => {
    if (typeof requestChain === "string") {
        try { return JSON.parse(requestChain); } catch { return null; }
    }
    return requestChain;
};
 
// Types
 
interface RequestDetailsProps extends RouteComponentProps<{ requestId: string }> {
    requestDetails: RequestDetailsState;
    requestDependencies: RequestDependenciesState;
    fetchRequestDetails: (requestId: string) => Promise<RequestDetails>;
    fetchSystemAdmins: () => void;
    getRequestApprovalStages: (requestId: number | string) => void;
    updateRequestApprovalStatus: (
        requestApprovalStageId: number | string,
        status: "PENDING" | "APPROVED" | "REJECTED",
        reason: string,
        assignedTo: number | string
    ) => Promise<any>;
    updateRequestApprover: (
        requestApprovalStageId: number | string,
        approverUser: number | string,
        approverPayload: any,
        requestType: string,
        assignedTo: number | string
    ) => Promise<any>;
    fetchAuditRequestApprovalLog: (requestId: number | string) => void;
    user?: AuthUser;
}
 
interface RequestDetailsComponentState {
    currentStep: number;
    selectedAdmin: string;
    isAssignModalOpen: boolean;
    initialLoadComplete: boolean;
    isRejectModalOpen: boolean;
    rejectionReason: string;
    isProcessing: boolean;
    approvalMessage: string;
    approvalMessageType: string;
    isUpdatingApprover: boolean;
}
 
class RequestDetailsComponent extends Component<RequestDetailsProps, RequestDetailsComponentState> {
    private messageTimeoutId: NodeJS.Timeout | null = null;
 
    state: RequestDetailsComponentState = {
        currentStep: 1,
        selectedAdmin: "all",
        isAssignModalOpen: false,
        initialLoadComplete: false,
        isRejectModalOpen: false,
        rejectionReason: "",
        isProcessing: false,
        approvalMessage: "",
        approvalMessageType: "",
        isUpdatingApprover: false,
    };
 
    componentDidMount() {
        this.loadInitialData();
    }
 
    componentDidUpdate(prevProps: RequestDetailsProps) {
        const { requestId } = this.props.match.params;
 
        if (prevProps.match.params.requestId !== requestId) {
            this.setState(
                {
                    initialLoadComplete: false,
                    selectedAdmin: "all",
                    approvalMessage: "",
                    approvalMessageType: "",
                    currentStep: 1,
                    isProcessing: false,
                    isUpdatingApprover: false,
                },
                this.loadInitialData
            );
            return;
        }
 
        this.handleInitialLoadComplete();
        this.handleStatusChange(prevProps);
        this.handleAssignedAdminChange(prevProps);
    }
 
    componentWillUnmount() {
        this.clearMessageTimeout();
    }
 
    // Data Loading
 
    private loadInitialData = async () => {
        const { requestId } = this.props.match.params;
        if (!requestId) return;
 
        if (this.props.user?.role === Constants.AB_SYSTEM_ADMIN) {
            this.props.fetchSystemAdmins();
        }
 
        try {
            const details = await this.props.fetchRequestDetails(requestId);
            if (details?.status) {
                this.setState({ currentStep: getStepFromStatus(details.status) });
            }
            this.props.getRequestApprovalStages(requestId);
            this.props.fetchAuditRequestApprovalLog(requestId);
        } catch (error) {
            console.error("Error loading initial data:", error);
        }
    };
 
    private refreshRequestData = () => {
        const { requestId } = this.props.match.params;
        return Promise.all([
            this.props.fetchRequestDetails(requestId),
            this.props.getRequestApprovalStages(requestId),
            this.props.fetchAuditRequestApprovalLog(requestId),
        ]);
    };
 
    // componentDidUpdate Handlers
 
    private handleInitialLoadComplete = () => {
        if (this.state.initialLoadComplete) return;
 
        const { requestDetails, requestDependencies } = this.props;
        const isDataReady =
            !requestDetails.loading && requestDetails.data &&
            !requestDependencies.loading && requestDependencies.systemAdmins;
 
        if (!isDataReady) return;
 
        const initialStep = getStepFromStatus(requestDetails.data!.status);
        const selectedAdmin = getInitialSelectedAdmin(requestDependencies.approvalStages);
 
        this.setState((prev) => ({
            initialLoadComplete: true,
            currentStep: prev.currentStep !== initialStep ? initialStep : prev.currentStep,
            selectedAdmin: prev.selectedAdmin !== selectedAdmin ? selectedAdmin : prev.selectedAdmin,
        }));
    };
 
    private handleStatusChange = (prevProps: RequestDetailsProps) => {
        if (!this.state.initialLoadComplete) return;
 
        const prevStatus = prevProps.requestDetails.data?.status;
        const nextStatus = this.props.requestDetails.data?.status;
 
        if (prevStatus !== nextStatus) {
            const newStep = getStepFromStatus(nextStatus);
            if (newStep !== this.state.currentStep) this.setState({ currentStep: newStep });
        }
    };
 
    private handleAssignedAdminChange = (prevProps: RequestDetailsProps) => {
        if (!this.state.initialLoadComplete) return;
 
        const prev = prevProps.requestDependencies.approvalStages?.assignedAdminId;
        const next = this.props.requestDependencies.approvalStages?.assignedAdminId;
 
        if (prev === next) return;
 
        const newSelectedAdmin = next != null ? String(next) : "all";
        if (this.state.selectedAdmin !== newSelectedAdmin) {
            this.setState({ selectedAdmin: newSelectedAdmin });
        }
    };
 
    // Visibility Logic
 
    private shouldShowApprovalSection = (): boolean => {
        if (this.props.requestDetails.data?.status === Constants.REQUEST_CANCELLED) return false;
 
        const { approvalStages } = this.props.requestDependencies;
        if (!approvalStages) return false;
 
        const { approver_user_id, approver_status, stage_order, request_status, requestType } = approvalStages;
 
        const isApprover = approver_user_id === this.props.user?.id;
        const isTerminalStatus =
            approver_status === Constants.STAGE_APPROVED ||
            approver_status === Constants.STAGE_REJECTED;
 
        if (isTerminalStatus) return false;
        if (!isApprover && stage_order === STAGE_ORDER.SECOND) return false;
        if (this.state.selectedAdmin === "all") return false;
        if (isApprover && stage_order === STAGE_ORDER.SECOND && approver_status === Constants.STAGE_PENDING) return false;
 
        return (
            isApprover &&
            stage_order === STAGE_ORDER.FIRST &&
            request_status === Constants.REQUEST_OPEN &&
            this.props.user?.role === Constants.AB_SYSTEM_ADMIN &&
            !!requestType && CHAIN_ACCESS_REQUEST_TYPES.has(requestType as Constants)
        );
    };
 
    private shouldShowAssignChainStoreApproval = (): boolean => {
        if (this.props.requestDetails.data?.status === Constants.REQUEST_CANCELLED) return false;
 
        const { approvalStages } = this.props.requestDependencies;
        if (!approvalStages) return false;
 
        const {
            approver_user_id, approver_status, stage_order, request_status,
            isFinalStage, action_code, assignedAdminId, requestType,
            chainInternalProjectAdminsUserIds,
        } = approvalStages;
 
        const userId = this.props.user?.id;
        const userRole = this.props.user?.role;
 
        const isChainAssociationAction =
            action_code === Constants.ACTION_ONLY_CHAIN_ASSOCIATION ||
            action_code === Constants.ACTION_ONLY_USER_ONBOARDING_AND_CHAIN_ASSOCIATION;
 
        const isChainAccessRequestType =
            requestType === Constants.CHAIN_ACCESS_BY_EXTERNAL_PROJECT_ADMIN ||
            requestType === Constants.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_PROJECT_ADMIN;
 
        const isFinalAction =
            isFinalStage === true &&
            isChainAssociationAction &&
            assignedAdminId === userId &&
            approver_status === Constants.STAGE_PENDING &&
            isChainAccessRequestType;
 
        if (isFinalAction) return true;
 
        const isValidStageOrder =
            stage_order === STAGE_ORDER.SECOND || stage_order === STAGE_ORDER.THIRD;
 
        const isUserInChainAdmins = userId
            ? (chainInternalProjectAdminsUserIds ?? []).includes(userId)
            : false;
 
        // Original logic: if approver_user_id exists, user must match; otherwise fall through to role check
        const isApproverMatch = approver_user_id
            ? approver_user_id === userId
            : isUserInChainAdmins || userRole !== Constants.AB_SYSTEM_ADMIN;
 
 
 
        const isActionableStatus =
            approver_status === Constants.STAGE_PENDING ||
            (approver_status === Constants.STAGE_APPROVED && request_status !== Constants.REQUEST_COMPLETED);
 
        const isAuthorised =
            isUserInChainAdmins || assignedAdminId == userId; // preserve loose equality
 
        return isApproverMatch && isValidStageOrder && isAuthorised && isActionableStatus;
    };
 
    private shouldShowAssignmentCard = (): boolean => {
        if (this.props.requestDetails.data?.status === Constants.REQUEST_CANCELLED) return false;
 
        const { approvalStages } = this.props.requestDependencies;
 
        if (this.props.user?.role !== Constants.AB_SYSTEM_ADMIN) return false;
        if (!approvalStages) return true;
 
        if (
            approvalStages.stage_order === STAGE_ORDER.FIRST &&
            approvalStages.status_code === Constants.STAGE_PENDING &&
            approvalStages.approver_status === Constants.STAGE_PENDING
        ) return true;
 
        if (approvalStages.approver_status === Constants.STAGE_REJECTED) return false;
 
        return true;
    };
 
    // Message Helpers
 
    private setMessage = (message: string, type: string, autoClear = false) => {
        this.clearMessageTimeout();
        this.setState({ approvalMessage: message, approvalMessageType: type });
 
        if (autoClear && message) {
            this.messageTimeoutId = setTimeout(() => {
                this.setState({ approvalMessage: "", approvalMessageType: "" });
            }, 3000);
        }
    };
 
    private clearMessageTimeout = () => {
        if (this.messageTimeoutId) {
            clearTimeout(this.messageTimeoutId);
            this.messageTimeoutId = null;
        }
    };
 
    private extractErrorMessage = (error: any, fallback: string): string =>
        error?.response?.data?.errors?.[0] ?? fallback;
 
    // API Handlers
 
    private handleAdminChange = async (value: string) => {
        const { approvalStages } = this.props.requestDependencies;
        if (approvalStages?.assignedAdminId != null) return;
 
        const approvalStageId = approvalStages?.approval_stage_id;
        if (!approvalStageId) {
            this.setMessage("Invalid approval stage ID. Cannot update approver.", "error");
            return;
        }
 
        this.setState({ isUpdatingApprover: true });
        this.setMessage("", "");
 
        try {
            await this.props.updateRequestApprover(
                approvalStageId, parseInt(value), "", approvalStages.requestType, parseInt(value)
            );
            await this.refreshRequestData();
            this.setState({ selectedAdmin: value });
            this.setMessage("Approver updated successfully", "success", true);
        } catch (error) {
            this.setMessage(this.extractErrorMessage(error, "Failed to update approver"), "error");
        } finally {
            this.setState({ isUpdatingApprover: false });
        }
    };
 
    private handleSubmitAssignment = async (assignmentData: AssignmentData) => {
        const { approvalStages } = this.props.requestDependencies;
        const approvalStageId = approvalStages?.approval_stage_id;
        const { data } = this.props.requestDetails;
 
        if (!approvalStageId) {
            this.setMessage("Invalid approval stage ID. Cannot assign request.", "error");
            return;
        }
 
        const approverPayload = data?.requestType
            ? {
                request_type: data.requestType,
                context: { payload_from: "APPROVER", approver_user_id: parseInt(this.state.selectedAdmin) },
                data: {
                    [data.requestType]: [{
                        requestor: data.request_initiator_user_id ?? data.email,
                        request_assigned_by: parseInt(this.state.selectedAdmin),
                        chain_id: assignmentData.chainId,
                        stores: assignmentData.storeIds,
                    }],
                },
            }
            : undefined;
 
        this.setState({ isProcessing: true });
        this.setMessage("", "");
 
        try {
            await this.props.updateRequestApprover(
                approvalStageId, parseInt(this.state.selectedAdmin),
                approverPayload, approvalStages!.requestType, ""
            );
            await this.refreshRequestData();
            this.handleCloseAssignModal();
            this.setMessage("Assignment submitted successfully", "success", true);
        } catch (error) {
            this.setMessage(this.extractErrorMessage(error, "Failed to submit assignment"), "error");
        } finally {
            this.setState({ isProcessing: false });
        }
    };
 
    private handleSubmitProjectAdminAssignment = async (assignmentData: AssignmentData) => {
        const { approvalStages } = this.props.requestDependencies;
        const approvalStageId = approvalStages?.approval_stage_id;
        const { data } = this.props.requestDetails;
 
        if (!approvalStageId) {
            this.setMessage("Invalid approval stage ID. Cannot assign request.", "error");
            return;
        }
 
        const requestChainParsed = parseRequestChain(data?.requestChain);
        const userData = requestChainParsed?.data?.USER_ONBOARDING_AND_CHAIN_ACCESS_BY_ANONYMOUS?.[0];
 
        const approverPayload = {
            request_type: data?.requestType,
            context: { payload_from: "APPROVER" },
            data: {
                [data?.requestType ?? Constants.CHAIN_ACCESS]: [{
                    requestor: data?.request_initiator_user_id ?? data?.email,
                    system_admin_id: parseInt(this.state.selectedAdmin),
                    chain_id: assignmentData.chainId,
                    stores: assignmentData.storeIds,
                    user_email: userData?.user_email,
                    phone_no: userData?.phone_no,
                    company_name: userData?.company_name,
                    first_name: userData?.first_name,
                    last_name: userData?.last_name,
                    user_role_id: assignmentData.role,
                    user_role: assignmentData.roleText,
                }],
            },
        };
 
        this.setState({ isProcessing: true });
        this.setMessage("", "");
        try {
            await this.props.updateRequestApprover(
                approvalStageId, "", approverPayload,
                approvalStages!.requestType, parseInt(this.state.selectedAdmin)
            );
            await this.refreshRequestData();
            this.handleCloseAssignModal();
            this.setMessage("Assignment submitted successfully", "success", true);
        } catch (error) {
            this.setMessage(this.extractErrorMessage(error, "Failed to submit assignment"), "error");
        } finally {
            this.setState({ isProcessing: false });
        }
    };
 
    private handleApprove = async () => {
        const { approvalStages } = this.props.requestDependencies;
        const approvalStageId = approvalStages?.approval_stage_id;
 
        if (!approvalStages || !approvalStageId) {
            this.setMessage(
                !approvalStages
                    ? "No approval stage found. Please ensure approval stages are loaded."
                    : "Invalid approval stage ID",
                "error"
            );
            return;
        }
 
        this.setState({ isProcessing: true });
        this.setMessage("", "");
 
        try {
            await this.props.updateRequestApprovalStatus(approvalStageId, "APPROVED", "", approvalStages.approver_user_id);
            await this.refreshRequestData();
            this.setMessage("Request has been approved successfully", "success");
        } catch (error) {
            this.setMessage(this.extractErrorMessage(error, "Failed to approve request"), "error");
        } finally {
            this.setState({ isProcessing: false });
        }
    };
 
    private handleSubmitRejection = async () => {
        const { rejectionReason } = this.state;
        const { approvalStages } = this.props.requestDependencies;
        const approvalStageId = approvalStages?.approval_stage_id;
 
        if (!rejectionReason.trim()) {
            this.setMessage("Please provide a reason for rejection", "error");
            return;
        }
        if (!approvalStages || !approvalStageId) {
            this.setMessage(
                !approvalStages
                    ? "No approval stage found. Please ensure approval stages are loaded."
                    : "Invalid approval stage ID",
                "error"
            );
            return;
        }
 
        this.setState({ isProcessing: true });
        this.setMessage("", "");
 
        try {
            await this.props.updateRequestApprovalStatus(approvalStageId, "REJECTED", rejectionReason, "");
            await this.refreshRequestData();
            this.handleCloseRejectModal();
            this.setMessage("Request has been rejected successfully", "rejected", true);
        } catch (error) {
            this.setMessage(this.extractErrorMessage(error, "Failed to reject request"), "error");
        } finally {
            this.setState({ isProcessing: false });
        }
    };
 
    // Modal Handlers
 
    private handleOpenAssignModal = () => this.setState({ isAssignModalOpen: true });
    private handleCloseAssignModal = () => this.setState({ isAssignModalOpen: false });
    private handleOpenRejectModal = () => this.setState({ isRejectModalOpen: true, rejectionReason: "" });
    private handleCloseRejectModal = () => this.setState({ isRejectModalOpen: false, rejectionReason: "" });
    private handleRejectionReasonChange = (value: string) => this.setState({ rejectionReason: value });
 
    // Render Helpers
 
    private renderMessage = () => {
        const { approvalMessage, approvalMessageType } = this.state;
        if (!approvalMessage) return null;
 
        const style = MESSAGE_STYLES[approvalMessageType] ?? DEFAULT_MESSAGE_STYLE;
 
        return (
            <div style={{
                padding: "16px",
                marginBottom: "16px",
                borderRadius: "8px",
                border: `1px solid ${style.borderColor}`,
                fontWeight: 500,
                fontSize: "12px",
                ...style,
            }}>
                {approvalMessage}
            </div>
        );
    };
 
    private renderStatusNotice = () => {
        const requestStatus = this.props.requestDetails.data?.status;
 
        if (requestStatus === Constants.REQUEST_CANCELLED) {
            return (
                <div style={{
                    padding: "16px",
                    marginBottom: "16px",
                    borderRadius: "8px",
                    backgroundColor: "#fee2e2",
                    border: "1px solid #ef4444",
                    color: "#991b1b",
                    fontWeight: 500,
                    textAlign: "center",
                }}>
                    Request is cancelled
                </div>
            );
        }
 
        const isRejectedRequest = requestStatus === Constants.REQUEST_REJECTED;
        const isRejectedStage =
            this.props.requestDependencies.approvalStages?.approver_status === Constants.STAGE_REJECTED;
 
        if (!isRejectedRequest && !isRejectedStage) return null;
 
        const decisionNote = this.props.requestDependencies.approvalStages?.decision_note ||
            this.props.requestDependencies.approvalStages?.comments;
 
        return (
            <div style={{
                padding: "16px",
                marginBottom: "16px",
                borderRadius: "8px",
                backgroundColor: "#fee2e2",
                border: "1px solid #ef4444",
                color: "#991b1b",
                textAlign: "left",
            }}>
                <div style={{
                    fontSize: "18px",
                    lineHeight: 1.2,
                    fontWeight: 700,
                    marginBottom: isRejectedRequest && decisionNote ? "12px" : "0",
                }}>
                    Request Rejected
                </div>
                {isRejectedRequest && decisionNote && (
                    <div style={{
                        backgroundColor: "#fff1f2",
                        border: "1px solid #fecdd3",
                        borderRadius: "6px",
                        padding: "10px 12px",
                    }}>
                        <div style={{
                            fontSize: "11px",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "#9f1239",
                            marginBottom: "4px",
                        }}>
                            Rejection reason
                        </div>
                        <div style={{
                            fontSize: "13px",
                            lineHeight: 1.45,
                            color: "#881337",
                            fontWeight: 500,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}>
                            {decisionNote}
                        </div>
                    </div>
                )}
            </div>
        );
    };
 
    private renderRejectedNotice = () => {
        return (
            <div style={{
                padding: "16px",
                marginBottom: "16px",
                borderRadius: "8px",
                backgroundColor: "#f3f4f6",
                border: "1px solid #d1d5db",
                color: "#1f2937",
                fontWeight: 500,
                textAlign: "center",
            }}>
                No actions are available for this request at this stage.
            </div>
        );
    };
 
    render() {
        const {
            currentStep, selectedAdmin, isAssignModalOpen, initialLoadComplete,
            isProcessing, isRejectModalOpen, rejectionReason, isUpdatingApprover,
        } = this.state;
 
        const { requestDetails, requestDependencies } = this.props;
        const { data, loading } = requestDetails;
 
        if (!initialLoadComplete && (loading || requestDependencies.loading)) return <Loader />;
        if (!data) return <NoDataAvailable />;
 
        const STEPS = getSteps({
            currentApproverRole: data.currentApproverRole,
            requestType: data.requestType ?? "",
            workInProgressBy: data.workInProgressBy,
        });
 
        const assignedAdminId = requestDependencies.approvalStages?.assignedAdminId;
        const displayedAdmin = assignedAdminId != null ? String(assignedAdminId) : selectedAdmin;
        const isAssignmentDisabled = assignedAdminId != null;
        const { requestId } = this.props.match.params;
 
        return (
            <div className="rd-page">
                <div className="rd-card">
                    <RequestHeader data={data} />
                    <div className="rd-header-divider" />
 
                    <div className="rd-main-wrapper">
                        {/* Sidebar */}
                        <div className="rd-sidebar">
                            <RequestInfoSidebar
                                data={data}
                                selectedAdmin={selectedAdmin}
                                onOpenAssignModal={this.handleOpenAssignModal}
                                onApprove={this.handleApprove}
                                isProcessing={isProcessing}
                            />
                        </div>
 
                        {/* Right content */}
                        <div className="rd-right-section">
                            <RequestStepper
                                steps={STEPS}
                                currentStep={currentStep}
                                fromStatusCode={data.fromStatusCode}
                                toStatusCode={data.toStatusCode}
                            />
 
                            {this.renderMessage()}
                            {this.renderStatusNotice()}
 
                            {this.shouldShowAssignmentCard() && (
                                <div className="rd-assignment-card-wrapper">
                                    <AssignmentCard
                                        key={requestId}
                                        selectedAdmin={displayedAdmin}
                                        systemAdmins={requestDependencies.systemAdmins}
                                        onAdminChange={this.handleAdminChange}
                                        isUpdating={isUpdatingApprover}
                                        isDisabled={isAssignmentDisabled}
                                        user={this.props.user}
                                    />
                                </div>
                            )}
 
                            {this.shouldShowApprovalSection() ? (
                                <SystemAdminApproval
                                    isProcessing={isProcessing}
                                    onReject={this.handleOpenRejectModal}
                                    onOpenAssignModal={this.handleOpenAssignModal}
                                />
                            ) : this.shouldShowAssignChainStoreApproval() ? (
                                <AssignChainStoreApproval
                                    isProcessing={isProcessing}
                                    onReject={this.handleOpenRejectModal}
                                    onApprove={this.handleApprove}
                                    stage={requestDependencies.approvalStages}
                                    onSuccess={this.refreshRequestData}
                                    handleSubmitProjectAdminAssignment={this.handleSubmitProjectAdminAssignment}
                                />
                            ) : (
                                requestDetails.data?.status === Constants.REQUEST_CANCELLED && this.renderRejectedNotice()
                            )}
                            <RequestAuditLogs auditLogs={requestDependencies.auditLogs} />
                        </div>
                    </div>
                </div>
 
                <SelectChainStoreInfo
                    isOpen={isAssignModalOpen}
                    onClose={this.handleCloseAssignModal}
                    onSubmit={this.handleSubmitAssignment}
                />
 
                <RejectModal
                    isOpen={isRejectModalOpen}
                    rejectionReason={rejectionReason}
                    isProcessing={isProcessing}
                    onReasonChange={this.handleRejectionReasonChange}
                    onSubmit={this.handleSubmitRejection}
                    onClose={this.handleCloseRejectModal}
                />
            </div>
        );
    }
}
 
// Redux
const mapStateToProps = (state: any) => ({
    requestDetails: state.requestDetails,
    requestDependencies: state.requestDependencies,
    user: state.auth_user.content,
});
 
const mapDispatchToProps = {
    fetchRequestDetails,
    fetchSystemAdmins,
    getRequestApprovalStages,
    updateRequestApprovalStatus,
    updateRequestApprover,
    fetchAuditRequestApprovalLog,
};
 
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RequestDetailsComponent));